<template>
  <div class="video-modal--docked" ref="videoModalDock">
  </div>
  <b-modal :destroy-on-hide="false" :model-value="showLiveVideoOverlay && !teleport" class="video-modal"
    :class="{ 'settings-mode': settingsMode, 'device-selection-mode': !hasDevice && settingsMode, 'full-screen': meetingMetaData.isFullScreen }"
    :can-cancel="[]">
    <teleport :to="$refs.videoModalDock" :disabled="!teleport">
      <div class="overlay-video popup-mode" ref="overlayVideo">
        <template v-if="settingsMode">
          <div class="overlay-video__header">
            <div></div>
            <div class="overlay-video__title">
              <span>{{ !hasDevice ? 'Select Mobile Video Device' : 'Video Meeting Settings' }}</span>
              <div class="overlay-video__subtitle" data-private v-if="hasDevice && !meetingMetaData.test_mode && headerName">
                {{ headerName }}
              </div>
            </div>
            <button class="button close-button is-danger is-rounded" @click="closeModal" v-if="!hasDevice">
              <span class="icon">
                <i class="mdi mdi-close"></i>
              </span>
            </button>
          </div>
          <ai-scorecard-bar
              v-if="!meetingMetaData.test_mode && meetingMetaData?.patient?.id"
              mode="modal"
              :scorecard="meetingMetaData.patient.ai_snapshot_scorecard"
              :ai-snapshot="meetingMetaData.patient.ai_snapshot"
              :ai-snapshot-updated-at="meetingMetaData.patient.ai_snapshot_updated_at"
              :patient-id="meetingMetaData.patient.id"
              :patient="meetingMetaData.patient"
              :patient-monitor="meetingMetaData.patient_monitor" />
          <div class="meet-settings__container">
            <template v-if="hasDevice">
              <b-message type="is-warning" v-if="!supportsTwoWayVideo">
                2 way video not setup on this device, the patient will only be able to hear you
              </b-message>
              <b-message type="is-danger" v-if="mediaError">
                {{ mediaErrorText }}
              </b-message>
              <b-message type="is-warning" v-if="activeMeeting.has_active">
                There may be an active video meeting in progress {{ activeMeeting.is_ptz ? 'on the PTZ camera' : '' }}
              </b-message>
              <b-message type="is-warning" style="width: 100%;" v-if="!isDeviceOnline">
                Video device is offline
              </b-message>
              <ld-form without-content-wrapper>
                <template #default="{ meta: { valid } }">
                  <input-with-validation b-name="checkbox" v-model="activateVideo" v-if="supportsTwoWayVideo && !meetingMetaData.embedded"
                    v-tooltip="{ content: 'Two-way video is unavailable - no camera was found or access was denied', disabled: !cameraUnavailable }"
                    :disabled="twoWayVideoRequired || cameraUnavailable">Two-Way
                    Video</input-with-validation>
                  <input-with-validation v-if="!meetingMetaData.test_mode" b-name="checkbox" v-model="doorKnock">Virtual
                    Doorbell</input-with-validation>
                  <input-with-validation v-if="meetingMetaData.device.ptz_enabled" b-name="checkbox"
                    v-model="isPtz">Pan-Tilt-Zoom Camera</input-with-validation>
                  <input-with-validation
                    v-if="!meetingMetaData.test_mode && availableVideoReasons.length > 0 && videoReasonVisibleOnJoin && !videoReasonMultiSelect"
                    v-model="selectedVideoReason" field-label="Video Reason" :rules="videoReasonRules" b-name="select"
                    expanded>
                    <option :value="null">Select video reason</option>
                    <option v-for="reason in availableVideoReasons" :value="reason.id" :key="reason.id">
                      {{ reason.name }}
                    </option>
                  </input-with-validation>
                  <input-with-validation ref="multipleVideoReasons" v-if="videoReasonMultiSelectVisible"
                    field-label="Video Reasons" :rules="videoReasonRules" class="video-reason-multiple-input">
                    <template #custom="{ handleChange }">
                      <ld-select v-model="selectedVideoReasons" :data="availableVideoReasons" option-key="id"
                        option-label="name" placeholder="Select video reasons" multiple
                        @update:modelValue="handleChange">
                      </ld-select>
                    </template>
                  </input-with-validation>
                  <div class="buttons-container mt-3">
                    <button class="button is-text is-outlined mr-1" @click="closeModal()">Cancel</button>
                    <button class="button is-success is-outlined" :disabled="!valid || micUnavailable || !isDeviceOnline"
                      v-tooltip="{ content: 'A microphone is required - none was found or access was denied', disabled: !micUnavailable }"
                      @click="() => { meetingMetaData.test_mode ? joinTest() : joinNormal() }">
                      Join
                    </button>
                    <button v-if="meetingPreviewEnabled" class="button is-outlined ml-3 is-warning"
                      v-tooltip="{ content: 'A microphone is required - none was found or access was denied', disabled: !micUnavailable }"
                      :disabled="activeMeeting.has_active || micUnavailable || !isDeviceOnline" @click="() => { joinPreview() }">
                      {{ previewButtonLabel }}
                    </button>
                  </div>
                </template>
              </ld-form>
            </template>
            <template v-else>
              <video-devices-table :table-id="null"
                :key="`video-devices-table-${patientDivisionId}-${meetingMetaData?.patient?.id}`"
                :filters="videoTableFilters" class="device-selection-table" :table-attributes="videoTableAttributes"
                :division-id="String(patientDivisionId)" is-standalone-component></video-devices-table>
              <button class="button is-success join-from-devices-table" v-show="selectedVideoDevice"
                @click="deviceSelected">Select Video Device</button>
            </template>
          </div>
        </template>
        <template v-else>
          <div class="overlay-video__block">
            <!-- v-show, not v-if: WebrtcView teleports the quality indicator into
                 .quality-indicator-teleport, so the target must stay in the DOM -->
            <div class="overlay-video__header" v-show="!meetingMetaData.embedded">
              <div class="overlay-video__title">
                <b-dropdown :disabled="!isPatientCameraPlaying || isExternal">
                  <template #trigger>
                    <i class="mdi mdi-dots-vertical"></i>
                  </template>
                  <template v-if="canSendIrCommands">
                    <b-dropdown-item custom class="tv-section-header">
                      <span>TV</span>
                    </b-dropdown-item>
                    <b-dropdown-item @click="sendTvPowerOnCommand">
                      <i class="mdi mdi-power"></i>
                      <span>Power On</span>
                    </b-dropdown-item>
                    <b-dropdown-item @click="sendTvPowerOffCommand">
                      <i class="mdi mdi-power-off"></i>
                      <span>Power Off</span>
                    </b-dropdown-item>
                    <b-dropdown-item v-if="showTvSwitchInput" @click="sendTvSwitchInputCommand">
                      <i class="mdi mdi-video-input-hdmi"></i>
                      <span>Switch Input</span>
                    </b-dropdown-item>
                    <b-dropdown-item @click="sendTvMuteCommand">
                      <i class="mdi mdi-volume-mute"></i>
                      <span>Mute/Unmute</span>
                    </b-dropdown-item>
                    <hr class="dropdown-divider">
                  </template>
                  <b-dropdown-item
                    v-if="$can('AccessLiveVideo', 'Meeting') || $can('AccessLiveVideo', 'PatientMonitor')" custom
                    class="volume-control">
                    <div>
                      <i class="mdi mdi-volume-high"></i>
                      <span>Volume</span>
                    </div>
                    <lvu-volume-control :model-value="meetingMetaData?.device?.config?.device?.volume || 100"
                      @volume-change="volumeChange"></lvu-volume-control>
                  </b-dropdown-item>
                  <b-dropdown-item v-if="meetingMetaData?.device?.id" @click="showVideoIssueDialog = true">
                    <i class="mdi mdi-alert-circle-outline" style="padding-right: 3px;"></i>
                    <span>Report a problem</span>
                  </b-dropdown-item>

                </b-dropdown>
                <div class="overlay-video__title-content">
                  <span>Patient Video</span>
                  <span class="overlay-video__patient-name" data-private>{{ headerName }}</span>
                </div>
              </div>
              <div class="overlay-video__buttons">
                <div class="quality-indicator-teleport"></div>
                <button v-if="isPreview" class="join-from-preview button is-success is-rounded close-button"
                  @click="convertPreviewToNormal">
                  join
                </button>
                <button v-if="$route.name != 'live-video.external'"
                  class="button is-primary is-rounded close-button teleport-button" @click="toggleTeleport">
                  <span class="icon">
                    <i class="mdi"
                      :class="teleport ? 'mdi-dock-window' : 'mdi-picture-in-picture-bottom-right-outline'"></i>
                  </span>
                </button>
                <button class="button is-primary is-rounded close-button" v-if="!meetingMetaData.test_mode && !isExternal"
                  @click="toggleSidebar(!$store.state.RightSidebar.isOpen)">
                  <span class="icon">
                    <i class="mdi mdi-page-layout-sidebar-right"></i>
                  </span>
                </button>
                <button class="button is-danger is-rounded close-button" @click="closeVideo">
                  <span class="icon">
                    <i class="mdi mdi-close"></i>
                  </span>
                </button>
                <!--            <div class="tag is-primary" v-if="isOutsideOfPatientPage && !isExternal" @click.stop="openPatientPage">-->
                <!--              <i class="mdi mdi-open-in-new" aria-hidden="true"></i>-->
                <!--              <span>Open Patient Page</span>-->
                <!--            </div>-->
              </div>
            </div>
            <div class="overlay-video__content-wrapper">
              <div class="overlay-video__content">
                <training-video-view v-if="isTrainingVideo" :key="webrtcViewReloadKey"
                  :video-settings="{ isPreview, visitorName, activateVideo, doorKnock, selectedVideoReason: videoReasonMultiSelect ? null : selectedVideoReason, selectedVideoReasons: videoReasonMultiSelect ? selectedVideoReasons : [], availableVideoReasons }"
                  :meeting-meta-data="meetingMetaData" start-automatically ref="webrtcView" @close="closeModal"
                  @meeting-id="handleMeetingId">
                </training-video-view>
                <webrtc-view v-else :key="webrtcViewReloadKey"
                  :video-settings="{ isPreview, visitorName, activateVideo: meetingMetaData.embedded ? false : activateVideo, doorKnock, startMuted: !!meetingMetaData.embedded, selectedVideoReason: videoReasonMultiSelect ? null : selectedVideoReason, selectedVideoReasons: videoReasonMultiSelect ? selectedVideoReasons : [], availableVideoReasons }"
                  :meeting-meta-data="meetingMetaData" :is-expanded="isPopupMode" :blinds-closed="blindsClosed"
                  start-automatically ref="webrtcView"
                  @close="closeModal" @hangup="closeModal()" @meeting-id="handleMeetingId">
                </webrtc-view>
              </div>
            </div>
          </div>
        </template>
      </div>
    </teleport>
  </b-modal>
  <video-issue-report-modal v-if="showVideoIssueDialog && meetingMetaData?.device?.id"
    :video-device-id="meetingMetaData.device.id" :patient-monitor-id="patientMonitorId || null"
    :meeting-id="meetingId" is-video-visit @close="showVideoIssueDialog = false" />
</template>

<script>
import { mapState, mapMutations } from "vuex";
import WebrtcView from "@/components/WebrtcView";
import TrainingVideoView from "@/components/TrainingVideoView";
import VideoIssueReportModal from "@/components/safety_center/VideoIssueReportModal.vue";
import InputWithValidation from "@/components/validation/InputWithValidation.vue";
import LdForm from "@/components/LdForm.vue";
import VideoDevicesTable from "@/views/hospital/video_devices/VideoDevicesTable";
import { FilterType, TableFilter, FilterValue } from "@/models/TableFilter";
import LvuVolumeControl from "@/components/LvuVolumeControl.vue";
import LdSelect from "@/components/LdSelect.vue";
import AiScorecardBar from "@/components/AiScorecardBar.vue";
import to from "@/lib/to";
import { setVolume } from "@/lib/webrtc";
import { SERVER_EVENT } from "@/lib/Pusher";
import * as semver from 'semver';

export default {
  name: "PatientOverlayVideo",
  components: { LvuVolumeControl, VideoDevicesTable, LdForm, InputWithValidation, WebrtcView, TrainingVideoView, LdSelect, AiScorecardBar, VideoIssueReportModal },
  computed: {
    ...mapState('LiveVideoStore', ['showLiveVideoOverlay', 'isPatientCameraPlaying', 'isPopupMode', 'isExternal', 'meetingMetaData', 'requestVideoStart']),
    canSendIrCommands() {
      return this.isPatientCameraPlaying &&
        !this.isExternal &&
        this.meetingMetaData.device?.config?.tv?.ir?.enabled === true &&
        this.$can('View', 'PatientMonitor')
    },
    supportsTwoWayVideo() {
      return this.meetingMetaData.kiosk_mode === true || this.meetingMetaData.device?.kind === 'kiosk'
    },
    tvInputActionName() {
      return this.meetingMetaData.device?.config?.tv?.ir?.ld_input_action
    },
    showTvSwitchInput() {
      return this.canSendIrCommands && this.tvInputActionName
    },
    meetingPreviewEnabled() {
      const divisionValue = this.meetingMetaData.division_meeting_preview_enabled
      return (divisionValue === true || this.$store.state.currentHospital?.meeting_preview_enabled !== false)
        && !this.basicMode
        && !this.meetingMetaData.test_mode
        && this.meetingMetaData.device?.kind !== 'kiosk'
        && !!this.meetingMetaData.device?.version && semver.gte(this.meetingMetaData.device.version, '1.19.0')
    },
    patientMonitorId() {
      // First try direct patient_monitor.id
      if (this.meetingMetaData.patient_monitor?.id) {
        return this.meetingMetaData.patient_monitor.id;
      }

      // Then try finding active monitor in patient.patient_monitors array
      if (this.meetingMetaData.patient?.patient_monitors) {
        const activeMonitor = this.meetingMetaData.patient.patient_monitors.find(monitor => monitor.status === 'active');
        if (activeMonitor) {
          return activeMonitor.id;
        }
      }

      console.warn('Patient monitor ID not found in meetingMetaData', this.meetingMetaData);
      return null;
    },
    isExternal() {
      return this.$route.name == 'live-video.external'
    },
    isOutsideOfPatientPage() {
      return this.$route.name != 'patient-monitors.monitoring'
    },
    headerName() {
      if (this.meetingMetaData.test_mode) {
        return ''
      } else {
        let name = this.$helpers.mask('patient_name', this.meetingMetaData?.patient.display_name)
        let roomName = this.meetingMetaData.patient_monitor?.room?.name || this.meetingMetaData.patient?.room?.name
        return `${name} / ${roomName}`
      }
    },
    hasDevice() {
      return !!this.meetingMetaData.device?.id
    },
    mediaErrorText() {
      if (!this.mediaError) {
        return ''
      }
      // only the microphone is required to join - a denied camera just disables two-way video
      return "Couldn't get access to your microphone. Make sure it's connected and microphone permissions have been granted."
    },
    isVideoReasonsValid() {
      return !!this.selectedVideoReason
    },
    patientDivisionId() {
      return this.meetingMetaData.patient_monitor?.division_id || this.meetingMetaData.patient?.division_id
    },
    videoReasonRequired() {
      return this.$store.state.currentHospital?.video_reason_required !== false
    },
    videoReasonVisibleOnJoin() {
      return this.$store.state.currentHospital?.video_reason_visible_on_join !== false
    },
    videoReasonMultiSelect() {
      return this.$store.state.currentHospital?.video_reason_multi_select === true
    },
    videoReasonRules() {
      return this.videoReasonRequired ? 'required' : ''
    },
    videoReasonMultiSelectVisible() {
      return !this.meetingMetaData.test_mode && this.availableVideoReasons.length > 0 && this.videoReasonVisibleOnJoin && this.videoReasonMultiSelect
    },
    isTrainingVideo() {
      return this.meetingMetaData?.device?.kind === 'trainingvideo'
    },
    previewButtonLabel() {
      return this.$store.state.currentHospital?.preview_button_label || 'Preview'
    },
    previewDefaultVideoReasonId() {
      return this.$store.state.currentHospital?.preview_default_video_reason_id || null
    },
    twoWayVideoRequired() {
      const hospital = this.$store.state.currentHospital
      if (!hospital?.two_way_video_required) {
        return false
      }

      const roleId = this.$store.state.currentUser?.role?.id
      const exemptIds = hospital.two_way_video_exempt_role_ids || []
      return !exemptIds.includes(roleId)
    },
    isDeviceOnline() {
      return this.meetingMetaData?.device?.status == 'online'
    }
  },
  methods: {
    ...mapMutations('LiveVideoStore', ['setIsMinimized', 'setShowLiveVideoOverlay', 'setIsPopupMode', 'setMeetingMetaData', 'setTestMeetingMetaData']),
    toggleTeleport() {
      this.teleport = !this.teleport
      this.setIsMinimized(this.teleport)
    },
    volumeChange(volume) {
      if (this.meetingMetaData.device.kind != 'trainingvideo') {
        setVolume(volume, this.$http, this.meetingMetaData.device.id);
      }
    },
    async sendTvIrCommand(action, successMessage) {
      if (!this.canSendIrCommands) {
        return;
      }

      const videoDeviceId = this.meetingMetaData.device?.id
      if (!videoDeviceId) {
        this.$buefy.toast.open({
          message: 'Unable to send TV command: video device not found',
          type: 'is-danger'
        });
        return;
      }

      const [err] = await to(this.$http.post(`/video_devices/${videoDeviceId}/send_tv_ir_command`, { ir_action: action }, { skipDivisions: true }))
      if (err) {
        console.error('Error sending IR command:', err);
        this.$buefy.toast.open({
          message: `Failed to send TV ${action} command`,
          type: 'is-danger'
        });
      } else {
        this.$buefy.toast.open({
          message: successMessage,
          type: 'is-success'
        });
      }
    },
    sendTvPowerOnCommand() {
      this.sendTvIrCommand('power_on', 'TV power on command sent');
    },
    sendTvPowerOffCommand() {
      this.sendTvIrCommand('power_off', 'TV power off command sent');
    },
    sendTvSwitchInputCommand() {
      this.sendTvIrCommand(this.tvInputActionName, `TV switched to ${this.tvInputActionName} input`);
    },
    sendTvMuteCommand() {
      this.sendTvIrCommand('mute', 'TV mute command sent');
    },
    openPatientPage() {
      this.$router.push({ name: 'patient-monitors.monitoring', params: { id: this.monitor.id } })
    },
    closeModal() {
      if (this.skipCloseEvent) {
        return;
      }
      this.initState()
      this.setShowLiveVideoOverlay(false)
    },
    closeVideo() {
      this.$refs.webrtcView.stopVideo()
      this.initState()
      this.setShowLiveVideoOverlay(false)
    },
    saveVideoSettings() {
      const settings = {
        activateVideo: this.activateVideo,
        doorKnock: this.doorKnock,
        isPtz: this.isPtz,
      }
      this.$store.dispatch('UserPreferencesStore/set', {
        key: 'video_meeting',
        value: settings
      })
    },
    loadVideoSettings() {
      const settings = this.$store.getters['UserPreferencesStore/getPreference']('video_meeting')
      if (!settings) {
        return
      }

      // Validate and apply activateVideo (only if device supports two way video)
      if ('activateVideo' in settings && this.supportsTwoWayVideo) {
        this.activateVideo = settings.activateVideo && this.supportsTwoWayVideo
      }

      // Validate and apply isPtz (only if device supports PTZ)
      if ('isPtz' in settings && this.meetingMetaData?.device?.ptz_enabled != null) {
        this.isPtz = settings.isPtz && this.meetingMetaData.device.ptz_enabled
      }

      // Apply doorKnock (no constraints)
      if ('doorKnock' in settings) {
        this.doorKnock = settings.doorKnock
      }
    },
    initState() {
      this.isPtz = Object.hasOwn(this.meetingMetaData?.device || {}, 'ptz_enabled') && this.meetingMetaData.device.ptz_enabled !== null ? this.meetingMetaData.device.ptz_enabled : false
      this.settingsMode = true
      this.visitorName = this.$store.state.currentUser.display_name || this.$store.state.currentUser.name
      this.activateVideo = Object.hasOwn(this.meetingMetaData || {}, 'kiosk_mode') && this.meetingMetaData.kiosk_mode !== null ? this.meetingMetaData.kiosk_mode : false
      this.doorKnock = true
      this.teleport = false
      this.settingsMode = true
      this.selectedVideoReason = this.meetingMetaData.video_reason_id ? parseInt(this.meetingMetaData.video_reason_id) : null
      this.selectedVideoReasons = []
      this.activeMeeting = {}
      this.mediaError = false
      this.micUnavailable = false
      this.cameraUnavailable = false
      this.setIsMinimized(this.teleport)
      // Load saved settings after setting defaults
      this.loadVideoSettings()
      // Force two-way video on for non-exempt roles when the hospital requires it
      if (this.twoWayVideoRequired && this.supportsTwoWayVideo) {
        this.activateVideo = true
      }
      // Embedded visits (mode 3) never send the viewer's camera — overrides
      // saved settings and the hospital requirement above
      if (this.meetingMetaData.embedded) {
        this.activateVideo = false
      }
    },
    async fetchVideoReasons() {
      const [err, response] = await to(this.$http.get('/video_reasons/dropdown'))
      if (!err) {
        this.availableVideoReasons = response.data

        if (this.selectedVideoReason) {
          const defaultReason = this.availableVideoReasons.find(r => r.id === this.selectedVideoReason)
          if (defaultReason) {
            this.selectedVideoReasons = [defaultReason]
          }
        }
      }
      if (this.videoReasonMultiSelectVisible) {
        this.$nextTick(() => {
          this.$refs?.multipleVideoReasons?.$refs?.field?.handleChange(this.selectedVideoReasons)
        })
      }
    },
    async checkActiveMeeting() {
      if (!this.meetingMetaData.patient?.id) {
        return
      }

      const [err, response] = await to(this.$http.get('/meetings/check_active', {
        params: { patient_id: this.meetingMetaData.patient.id }
      }))

      if (!err) {
        this.activeMeeting = response.data
        this.basicMode = response.data.basic_mode
      }
    },
    checkedDeviceUpdate(rows) {
      if (rows.length > 1) {
        this.videoTableAttributes.checkedRows = [rows.pop()]
      } else {
        this.videoTableAttributes.checkedRows = rows
      }
      this.selectedVideoDevice = this.videoTableAttributes.checkedRows[0];
    },
    isTableChecked(a, b) {
      return a.id == b.id
    },
    deviceSelected() {
      if (this.selectedVideoDevice) {
        this.settingsMode = true
        this.setMeetingMetaData({
          ...this.meetingMetaData,
          device: this.selectedVideoDevice,
          kiosk_mode: this.selectedVideoDevice.state?.kiosk_mode
        })
        // initState() ran before a device existed, so re-apply the two-way video requirement
        if (this.twoWayVideoRequired && this.supportsTwoWayVideo) {
          this.activateVideo = true
        }
      }
    },
    setupMoveEvents() {
      let offsetY, offsetX
      const mouseMove = ev => {
        this.$refs.overlayVideo.style.top = (ev.clientY + offsetY) + 'px'
        this.$refs.overlayVideo.style.left = (ev.clientX + offsetX) + 'px'
      }
      this.$refs.overlayVideo.addEventListener('mousedown', ev => {
        if (this.teleport) {
          offsetY = this.$refs.overlayVideo.offsetTop - ev.clientY
          offsetX = this.$refs.overlayVideo.offsetLeft - ev.clientX
          window.document.addEventListener('mousemove', mouseMove)
        }
      })
      document.addEventListener('mouseup', () => {
        window.document.removeEventListener('mousemove', mouseMove)
      })
    },
    async joinNormal() {
      // Save user's settings for next time
      this.saveVideoSettings()

      const mediaResults = await this.checkMediaPermissions()
      if (mediaResults.err) {
        this.mediaError = mediaResults
        return false
      }
      const metaData = { ...this.meetingMetaData, is_ptz: this.isPtz, previewMode: false }
      if (this.meetingMetaData?.test_mode) {
        this.setTestMeetingMetaData(metaData)
      } else {
        this.setMeetingMetaData(metaData)
      }
      this.isPreview = false
      this.settingsMode = false
    },
    async joinPreview() {
      // Auto-select default preview video reason if configured and none selected
      if (this.previewDefaultVideoReasonId && this.availableVideoReasons.length > 0) {
        if (this.videoReasonMultiSelect) {
          if (this.selectedVideoReasons.length === 0) {
            const defaultReason = this.availableVideoReasons.find(r => r.id === this.previewDefaultVideoReasonId)
            if (defaultReason) {
              this.selectedVideoReasons = [defaultReason]
            }
          }
        } else {
          if (!this.selectedVideoReason) {
            this.selectedVideoReason = this.previewDefaultVideoReasonId
          }
        }
      }

      // Save user's settings for next time
      this.saveVideoSettings()

      const mediaResults = await this.checkMediaPermissions()
      if (mediaResults.err) {
        this.mediaError = mediaResults
        return false
      }
      // save the setting for use when joining from preview
      this.joinFromPreviewMetadata = {
        activateVideo: this.activateVideo,
        doorKnock: this.doorKnock,
        isPtz: this.isPtz,
      }
      console.debug('joining in preview mode', this.joinFromPreviewMetadata)
      // set the meeting metadata to indicate preview mode
      const metaData = { ...this.meetingMetaData, is_ptz: this.isPtz, previewMode: true }
      this.setMeetingMetaData(metaData)
      this.isPreview = true
      this.settingsMode = false
    },
    async convertPreviewToNormal() {
      console.debug('converting preview to normal join', this.joinFromPreviewMetadata)
      const success = await this.$refs.webrtcView.convertPreviewToNormal()
      if (!success) {
        console.warn('could not convert preview to normal')
        return
      }
      this.isPreview = false
      // this.settingsMode = false
    },
    async joinTest() {
      this.setTestMeetingMetaData({ ...this.meetingMetaData, is_ptz: this.isPtz, previewMode: false })
      this.isPreview = false
      this.settingsMode = false
    },
    async queryExistingPermissions() {
      const descriptors = ['microphone', 'camera']
      return Promise.all(descriptors.map(name => navigator.permissions.query({ name })))
    },
    getMediaStates(permissions) {
      return {
        audio: permissions[0].state,
        video: permissions[1]?.state
      }
    },
    // passively check device permissions and presence (no browser prompt) so we can warn
    // about a denied or missing device before the user clicks Join
    async previewMediaPermissions() {
      const [permErr, perms] = await to(this.queryExistingPermissions())
      const [devErr, devices] = await to(navigator.mediaDevices.enumerateDevices())

      const states = permErr ? {} : this.getMediaStates(perms)
      // device presence is reported even before permission is granted (labels are hidden, kinds
      // are not). default to "present" if enumeration fails so we never wrongly block the user.
      const hasMic = devErr ? true : devices.some(d => d.kind === 'audioinput')
      const hasCamera = devErr ? true : devices.some(d => d.kind === 'videoinput')

      // unavailable = explicitly denied OR no hardware present
      this.micUnavailable = states.audio == 'denied' || !hasMic
      this.cameraUnavailable = states.video == 'denied' || !hasCamera
      // can't do two-way video without a camera - drop to audio-only so the user can still connect
      if (this.cameraUnavailable) {
        this.activateVideo = false
      }
      // only an unavailable microphone blocks connecting; an unavailable camera just disables two-way video
      this.mediaError = this.micUnavailable ? { err: true, ...states } : false
    },
    async checkMediaPermissions() {
      const perms = await this.queryExistingPermissions()
      const states = this.getMediaStates(perms)

      // audio is always needed; camera is only needed for two-way video
      const videoNeeded = this.activateVideo

      // block up front if a device we actually need has been explicitly denied.
      // checking denied before prompt avoids falsely passing pre-flight when one
      // device is denied while the other is still 'prompt' but unneeded
      if (states.audio == 'denied' || (videoNeeded && states.video == 'denied')) {
        return Promise.resolve({ err: true, ...states })
      }

      const shouldRequestAudio = states.audio == 'prompt'
      // NOTE: if we ask for video and there is no camera, it will throw an error
      // so don't ask for camera if we don't need it
      const shouldRequestVideo = states.video == 'prompt' && videoNeeded

      // needed devices are already granted - nothing left to prompt for
      if (!shouldRequestAudio && !shouldRequestVideo) {
        return Promise.resolve({ err: false, ...states })
      }

      this.$store.commit('toggleLoading', { loadingState: true })
      const [err, mediaStream] = await to(navigator.mediaDevices.getUserMedia({
        audio: shouldRequestAudio,
        video: shouldRequestVideo
      }));
      this.$store.commit('toggleLoading', { loadingState: false })
      if (err) {
        const newStates = this.getMediaStates(await this.queryExistingPermissions())
        // reflect a denied camera in the UI immediately - uncheck and disable two-way video.
        // OR-in so a failed attempt never clears an unavailability detected on open (e.g. no hardware)
        this.micUnavailable = this.micUnavailable || newStates.audio == 'denied'
        this.cameraUnavailable = this.cameraUnavailable || newStates.video == 'denied'
        if (this.cameraUnavailable) {
          this.activateVideo = false
        }
        // only an unavailable microphone blocks the call; an unavailable camera just drops us to audio-only
        if (this.micUnavailable) {
          return Promise.resolve({ err: true, errorName: err.name, ...newStates })
        }
        return Promise.resolve({ err: false, ...newStates })
      }
      mediaStream.getTracks().forEach(track => track.stop())
      return Promise.resolve({ err: false, ...states })
    },
    toggleSidebar(state = null) {
      if (state && this.meetingId) {
        this.$store.dispatch('RightSidebar/open', {
          component: 'video-visit-sidebar',
          props: {
            monitorId: this.patientMonitorId,
            patientId: this.meetingMetaData?.patient?.id,
            meetingId: this.meetingId
          }
        })
      } else {
        this.$store.dispatch('RightSidebar/close')
      }
    },
    handleMeetingId(id) {
      this.meetingId = id
      if (id && !this.settingsMode && this.showLiveVideoOverlay && !this.isExternal) {
        this.toggleSidebar(true)
      }
    },
    handleCurtainToggled({ monitor_id, is_private }) {
      if (this.patientMonitorId && monitor_id === this.patientMonitorId) {
        this.blindsClosed = is_private
      }
    },
    deviceStatusChanged({ video_device_id, status }) {
      const device = this.meetingMetaData?.device
      if (!video_device_id || !status || device?.id != video_device_id) {
        return
      }

      const metaData = { ...this.meetingMetaData, device: { ...device, status } }
      if (this.meetingMetaData.test_mode) {
        this.setTestMeetingMetaData(metaData)
      } else {
        this.setMeetingMetaData(metaData)
      }
    }
  },
  beforeMount() {
    this.initState()
  },
  beforeUnmount() {
    this.pusherHospitalChannel?.unbind(SERVER_EVENT.CURTAIN_TOGGLED, this.handleCurtainToggled)
    this.pusherHospitalChannel?.unbind(SERVER_EVENT.DEVICE_STATUS_CHANGED, this.deviceStatusChanged)
  },
  watch: {
    showLiveVideoOverlay(newVal) {
      if (newVal) {
        this.teleport = false
        this.initState()
        this.fetchVideoReasons()
        this.checkActiveMeeting()
        this.previewMediaPermissions()
        if (this.requestVideoStart) {
          this.settingsMode = !this.requestVideoStart
          this.doorKnock = false
        }

        this.blindsClosed = this.meetingMetaData?.patient_monitor?.is_private === true
        if (!this.pusherHospitalChannel) {
          const channel = `private-encrypted-ld-${import.meta.env.VITE_ENVIRONMENT_NAME}-hospital-${this.$store.state.currentUser.hospital_id}`
          this.pusherHospitalChannel = this.$pusher.subscribe(channel)
          this.pusherHospitalChannel.bind(SERVER_EVENT.CURTAIN_TOGGLED, this.handleCurtainToggled)
        }
        this.pusherHospitalChannel.bind(SERVER_EVENT.DEVICE_STATUS_CHANGED, this.deviceStatusChanged)

        this.$nextTick(() => {
          this.setupMoveEvents()
        })
      } else {
        this.pusherHospitalChannel?.unbind(SERVER_EVENT.DEVICE_STATUS_CHANGED, this.deviceStatusChanged)
      }
    },
    settingsMode() {
      if (this.meetingMetaData.test_mode || this.isExternal) {
        return
      }

      if (this.settingsMode && !this.showLiveVideoOverlay) {
        this.toggleSidebar(false)
      } else if (!this.settingsMode && this.showLiveVideoOverlay) {
        this.toggleSidebar(true)
      }
    },
    activateVideo() {
      // re-check when two-way video is toggled, since that changes whether the camera is needed
      if (this.showLiveVideoOverlay) {
        this.previewMediaPermissions()
      }
    }
  },
  data() {
    return {
      skipCloseEvent: false,
      webrtcViewReloadKey: 1,
      teleport: false,
      settingsMode: true,
      visitorName: '',
      activateVideo: true,
      doorKnock: true,
      isPtz: false,
      basicMode: true,
      activeMeeting: {},
      videoTableAttributes: {
        checkable: true,
        isInnerTable: true,
        headerCheckable: false,
        checkedRows: [],
        'onUpdate:checked-rows': this.checkedDeviceUpdate,
        customIsChecked: this.isTableChecked,
        defaultFilters: {
          is_active: [new FilterValue({ value: 'false' })],
          kind: [new FilterValue({ value: 'kiosk' }), new FilterValue({ value: 'mobilecart' }), new FilterValue({ value: 'mobile3rdparty' })],
          service_status: [new FilterValue({ value: 'in_service' })],
        },
        filterSettings: {
          forceDefaultFilters: true,
          alwaysAppendDefaultFilters: true
        }
      },
      videoTableFilters: [
        new TableFilter({ name: 'name', displayName: "Name", type: FilterType.TEXT }),
      ],
      selectedVideoDevice: null,
      mediaError: false,
      micUnavailable: false,
      cameraUnavailable: false,
      availableVideoReasons: [],
      selectedVideoReason: null,
      selectedVideoReasons: [],
      meetingId: null,
      showVideoIssueDialog: false,
      joinFromPreviewMetadata: {},
      isPreview: false,
      blindsClosed: false,
      pusherHospitalChannel: null,
    }
  }
}
</script>

<style scoped lang="scss">
@keyframes liveVideo {
  from {
    opacity: 0.5;
  }

  to {
    opacity: 1;
  }
}

.overlay-video {
  width: auto;
  background: var(--ld-bg-secondary);
  position: fixed;
  right: 0;
  top: 90px;
  border-radius: 5px;
  box-shadow: 0px -1px 10px -2px var(--ld-shadow-color);
  //transition: all 0.1s linear;
  z-index: 100;
}

.overlay-video__content-wrapper {
  // killing 1 extra unknown pixel from height. it should be standard 16/9 ratio 56.25%
  padding-top: 56.24%;
  position: relative;
  opacity: 1;
  transition: all 0.1s linear;

  @include until($desktop) {
    padding-top: 0;
    aspect-ratio: 16 / 9;
    max-height: 80vh;
  }
}

.overlay-video__content {
  position: absolute;
  top: 0;
  width: 100%;
  height: 100%;

  :deep(> div) {
    width: 100%;
    height: 100%;
  }
}

.overlay-video__header {
  padding: 14px 20px 11px;
  border-bottom: 1px solid var(--ld-bg-primary);
  background: linear-gradient(to bottom, transparent, color-mix(in srgb, var(--ld-bg-primary) 42%, transparent));
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
}

.close-button i {
  font-size: 17px;
}

.overlay-video__title {
  display: flex;
  align-items: center;
  margin-left: -7px;

  :deep(.dropdown) {
    margin-right: 7px;
  }
}

.overlay-video__title-content {
  display: flex;
  flex-direction: column;
  font-size: 12px;
  text-transform: uppercase;
  font-weight: 500;
  letter-spacing: 1.1px;
}

.overlay-video__patient-name {
  font-size: 11px;
  opacity: 0.9;
  text-transform: none;
  padding-top: 3px;
  font-weight: 500;
  overflow-wrap: anywhere;
}

.overlay-video__title--animated {
  i {
    color: var(--ld-status-danger);
    animation-name: liveVideo;
    animation-duration: 0.5s;
    animation-direction: alternate;
    animation-iteration-count: infinite;
  }
}

.overlay-video__buttons {
  display: flex;
  align-items: center;

  .tag {
    font-size: 10px;
    display: flex;
    align-items: center;
    line-height: 1;
    height: 2.1em;

    &:not(:last-child) {
      margin-right: 15px;
    }

    i {
      font-size: 12px;
      padding-right: 3px;
    }
  }
}

.overlay-video.popup-mode {
  position: static;
  width: 60%;
  margin: 0 auto;

  @include until($desktop) {
    width: calc(100% - 40px);
    margin: 0 20px;
  }
}

.video-modal :deep(.modal-content) {
  transition: all 0.1s;
  width: 100% !important;
}

.meet-settings__container {
  padding: 8px 20px 20px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  // overflow-y: auto !important;

  .field-wrapper {
    width: 170px;
  }
  :deep(.form-box) {
    display: flex;
    flex-direction: column;
    align-items: center;
  }
}

.overlay-video__subtitle {
  font-size: 13px;
  font-weight: 400;
  margin-top: 5px;
  opacity: 0.7;
  padding: 0 16px;
}

.settings-mode {
  :deep() {
    .overlay-video__header {
      justify-content: center;
      padding: 18px 0 15px;
    }

    .overlay-video__title {
      flex-direction: column;
    }

    .modal-content {
      width: 500px !important;
    }
  }
}

.close-button {
  width: 26px !important;
  height: 26px !important;
  margin-left: 10px;
}

.device-selection-mode {
  :deep() {
    .overlay-video {
      height: 100% !important;
      overflow-y: hidden !important;
      display: flex;
      flex-direction: column;
      width: auto !important;
      max-width: 1080px;
    }

    .overlay-video__header {
      justify-content: space-between;
      padding: 10px 20px 5px;
    }

    .meet-settings__container {
      flex: 1 1;
      overflow-y: auto;

      >.device-selection-table {
        display: flex;
        flex-direction: column;
        height: 100%;
        width: 100%;

        .b-table {
          flex: 1 1;
          display: flex;
          flex-direction: column;
          padding-bottom: 20px;

          .table-wrapper {
            overflow: auto;
            flex: 1 1;
          }
        }
      }
    }

    .modal-content {
      max-width: 1200px !important;
      width: 100% !important;
    }
  }
}

.join-from-devices-table {
  position: absolute;
  bottom: 22px;
}

.volume-control {
  i {
    padding-right: 3px;
  }
}

.tv-section-header {
  font-weight: 600;
  font-size: 12px;
  text-transform: uppercase;
  color: var(--ld-text-secondary);
  padding: 8px 16px 4px 16px !important;
  cursor: default !important;

  &:hover {
    background-color: transparent !important;
  }
}

.video-modal--docked .overlay-video__block {
  background: var(--ld-bg-secondary);
  box-shadow: 0px -1px 10px -2px var(--ld-shadow-color);
  border-radius: 5px;
}

.video-modal--docked .overlay-video {
  position: absolute;
  right: 0;
  bottom: 0;
  top: auto;
  margin: 0;
  width: 30%;
  z-index: 9999;
  background: transparent;
  box-shadow: none;
}

.modal.full-screen {
  display: block;

  :deep() {
    .modal-content {
      max-width: none !important;
      width: 100% !important;
      height: 100%;
      max-height: none;
      margin: 0;
    }

    .overlay-video {
      width: 100% !important;
      max-width: none;
      height: 100%;
    }

    .meet-settings__container {
      height: 100%;
    }

    video.training-video {
      height: 100%;
      //width: auto;
    }

    .overlay-video__block {
      display: flex;
      flex-direction: column;
      height: 100%;
    }

    .overlay-video__content-wrapper {
      padding: 0;
      height: 100%;
    }

    .webrtc-view__video-container {
      height: 100%;
    }
  }
}

.buttons-container {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: -15px;
}

:deep(.field-wrapper) {
  margin-bottom: 12px;
}

@include until($desktop) {
  .overlay-video.popup-mode {
    width: calc(100% - 40px);
    margin: 0 20px;
  }

  .teleport-button {
    display: none;
  }

  .settings-mode :deep(.modal-content) {
    width: auto !important;
  }
}

.video-modal:not(.settings-mode) {
  .overlay-video {
    width: 80%;
  }
}

.video-reason-multiple-input {
  :deep(.ld-multi-select) {
    width: 100%;
  }

  :deep(.ld-multi-select > div) {
    width: 100%;
  }

  :deep(.dropdown-trigger button) {
    white-space: pre-line;
    height: auto;
    text-align: left;
    width: 100%;
  }
}

.join-from-preview {
  text-transform: uppercase;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 1px;
  padding: 0 30px !important;
}
</style>
